import {
  useId,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

/**
 * Lightweight, accessible tooltip wrapper.
 *
 * Wrap any inline element to surface a small contextual hint on hover/focus.
 * Use this instead of the native `title` attribute when you want a reliable,
 * styled tooltip — `title` is slow, easy to miss, and inconsistent across
 * browsers/OSes.
 *
 * Usage:
 *   <Tooltip label="Pick a different layout for this template">
 *     <button onClick={openPicker}>Change</button>
 *   </Tooltip>
 *
 * The wrapper renders an inline `<span>` around the child, so it's safe to
 * use inside flex/grid rows next to other inline content. The tooltip itself
 * is `pointer-events: none` so it never steals clicks from the child.
 */
export interface TooltipProps {
  /** The hint to show. Keep it short — one line is ideal. */
  label: ReactNode;
  /**
   * Which side of the trigger to render the tooltip on.
   *   - `'top'`    (default) — above the trigger
   *   - `'bottom'`           — below the trigger
   */
  side?: 'top' | 'bottom';
  /**
   * When true, wraps text at a narrow max width with a larger font (better for
   * sentence-length hints). Default keeps a single compact line.
   */
  wrap?: boolean;
  /** The element being annotated. */
  children: ReactElement;
}

export default function Tooltip({
  label,
  side = 'top',
  wrap = false,
  children,
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const id = useId();

  const positionCls =
    side === 'top'
      ? `bottom-full ${wrap ? 'mb-2' : 'mb-1'} left-1/2 -translate-x-1/2`
      : `top-full ${wrap ? 'mt-2' : 'mt-1'} left-1/2 -translate-x-1/2`;
  const arrowCls =
    side === 'top'
      ? 'top-full left-1/2 -translate-x-1/2 -translate-y-1/2 rotate-45'
      : 'bottom-full left-1/2 -translate-x-1/2 translate-y-1/2 rotate-45';

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      {/* Annotate the child for screen readers without leaking the tooltip
          into normal flow. We don't clone the child because that's brittle
          across React versions; aria-describedby on the wrapper is enough
          for AT to surface the tip when the child has focus. */}
      <span aria-describedby={open ? id : undefined} className="inline-flex">
        {children}
      </span>
      <span
        id={id}
        role="tooltip"
        className={`pointer-events-none absolute z-[90] rounded-lg bg-gray-900 dark:bg-gray-700 text-white font-medium shadow-lg transition-opacity duration-100 ${
          wrap
            ? 'max-w-[18rem] whitespace-normal text-center text-sm leading-normal px-3.5 py-2.5'
            : 'whitespace-nowrap text-xs px-2.5 py-1.5'
        } ${open ? 'opacity-100' : 'opacity-0'} ${positionCls}`}
      >
        {label}
        <span
          className={`absolute ${wrap ? 'w-2.5 h-2.5' : 'w-2 h-2'} bg-gray-900 dark:bg-gray-700 ${arrowCls}`}
          aria-hidden="true"
        />
      </span>
    </span>
  );
}
